<template>
  <div class="flex items-center w-full relative">
    <div :key="`search-${searchIncrement}`" class="w-full">
      <ClientOnly>
        <Combobox v-model="selected" multiple>
          <div class="relative">
            <div
              class="flex relative w-full cursor-default overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm h-[48px] justify-end"
              :class="noOverlay ? '' : 'z-50'"
            >
              <ComboboxInput
                id="input"
                class="input-search w-full px-4 py-3 rounded-lg   border focus:outline-none"
                :class="{
                  'border-white': focused && !noOverlay,
                  'border-gray-400': !focused || noOverlay,
                  disabled: isDisabled,
                }"
                :disabled="isDisabled"
                :placeholder="t('DjSearchBar.placeholder')"
                @change="query = $event.target.value"
                @focus="onFocus"
                @click="onFocus"
                @blur="onBlur"
                @keyup.enter="onSearch"
              />
            </div>
            <div v-if="focused && !noOverlay" class="fixed inset-0 blured z-40" @click.stop />

            <TransitionRoot
              leave="transition ease-in duration-100"
              leave-from="opacity-100"
              leave-to="opacity-0"
            >
              <div v-show="openOptions">
                <ComboboxOptions
                  id="options"
                  :class="{ 'z-50': noOverlay, 'h-sm': smallOptions }"
                  class="dj-searchbar-result absolute mt-1 w-full z-50"
                  static
                >
                  <div class="dj-searchbar-result-content">
                    <div
                      class="dj-searchbar-products"
                      :class="{
                        'w-1/2': selectedProductId !== '',
                        'w-full': selectedProductId === '',
                      }"
                    >
                      <div
                        v-if="(!productsList || productsList.length === 0) && query !== ''"
                        class="relative cursor-default select-none px-4 py-2 text-gray-700"
                      >
                        {{ $t('DjSearchBar.noData') }}
                      </div>
                      <div v-else class="dj-searchbar-products-title">
                        {{ $t('DjSearchBar.products') }}
                      </div>
                      <ComboboxOption v-if="loading" as="template" :value="loading">
                        <li class="relative">
                          <span class="block">
                            <div class="dj-searchbar-products-list relative">
                              <div class="flex items-center space-x-4">
                                <USkeleton class="h-12 w-12" :ui="{ rounded: 'rounded-full' }" />
                                <div class="space-y-2">
                                  <USkeleton class="h-4 w-[250px]" />
                                  <USkeleton class="h-4 w-[200px]" />
                                </div>
                              </div>
                            </div>
                          </span>
                        </li>
                      </ComboboxOption>
                      <div v-else class="dj-searchbar-result_list overflow-auto pr-3">
                        <ComboboxOption
                          v-for="(product, index) in productsList"
                          :key="index"
                          as="template"
                          :value="product"
                        >
                          <li class="relative last:mb-2">
                            <span class="block">
                              <slot name="result" :row="product">
                                <div class="dj-searchbar-products-list relative">
                                  <DjProductSearchResult
                                    :product="product"
                                    :open="selectedProductId === product.product.externalId"
                                    :small="selectedProductId !== ''"
                                    :has-variants="hasVariants(product)"
                                    @select-product="selectProduct"
                                  />
                                </div>
                              </slot>
                            </span>
                          </li>
                        </ComboboxOption>
                      </div>
                    </div>
                    <div v-if="selectedProductId !== '' && !hasVariants" class="w-1/2">
                      <div class="dj-searchbar-product-variants-title">
                        {{ $t('DjSearchBar.references') }}
                      </div>
                      <li class="dj-searchbar-result_list relative overflow-auto pr-3">
                        <span
                          v-for="(variant, index) in variants"
                          :key="`reference-${index}-${increment}`"
                          class="block last:mb-2"
                        >
                          <div class="dj-searchbar-product-variants-list relative">
                            <DjProductSearchResultVariant :variant="variant" />
                          </div>
                        </span>
                      </li>
                    </div>
                  </div>
                </ComboboxOptions>
              </div>
            </TransitionRoot>
          </div>
        </Combobox>
      </ClientOnly>
    </div>
  </div>
</template>

<script setup lang="ts">
  import {
    Combobox,
    ComboboxInput,
    ComboboxOptions,
    ComboboxOption,
    ComboboxButton,
    TransitionRoot,
  } from '@headlessui/vue';
  import { useProduct } from '@/composables/useProduct';
  import { AggregationType } from '@djust-b2b/djust-front-sdk/lib/services/product/definitions';
  import type { ProductVariantResponse } from '@djust-b2b/djust-front-sdk/lib/interfaces/models/product';

  const props = defineProps({
    buttonIcon: {
      type: String,
      default: '',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    noSubmitBtn: {
      type: Boolean,
      default: false,
    },
    noVariant: {
      type: Boolean,
      default: false,
    },
    noOverlay: {
      type: Boolean,
      default: false,
    },
    smallOptions: {
      type: Boolean,
      default: false,
    },
    allwaysDisplaySearchBar: {
      type: Boolean,
      default: false,
    },
  });

  const { getProductVariants } = useProduct();
  const { autoCompleteSearchProducts } = useSearchProduct();
  const router = useRouter();
  const { locale } = useI18n();
  const { t } = useI18n();
  const searchIncrement = ref(0);
  const increment = ref(0);
  const searchButtonClicked = ref(false);
  const productsList = ref<ProductSearchContent[]>([]);
  const loading = ref(false);
  const { getUserCurrency } = useUser();
  const currency = computed(() => getUserCurrency());
  const focused = ref(false);
  const query = ref('');
  const selectedProductId = ref('');
  const variants = ref<ProductVariantResponse[]>([]);
  const selected = ref();
  const openOptions = ref(false);
  const displaySearchBar = ref<boolean>(true);
  const localePath = useLocalePath();
  const fetchProducts = async () => {
    try {
      loading.value = true;
      const autoCompleteParams: AutocompleteProductsRequest = {
        input: query.value,
        locale: locale.value,
        currency: currency.value,
        aggregation: AggregationType.PRODUCT,
      };
      const searchProductsResponse = await autoCompleteSearchProducts(autoCompleteParams);
      productsList.value = searchProductsResponse.products;
    } catch (error) {
    } finally {
      loading.value = false;
    }
  };

  watch(query, async (newQuery) => {
    if (newQuery.length > 0) {
      await fetchProducts();
      openOptions.value = true;
    } else if (newQuery.length === 0) {
      openOptions.value = false;
      productsList.value = [];
    } else {
      productsList.value = [];
    }
  });

  onMounted(() => {
    updateDisplaySearchBar();
    window.addEventListener('resize', updateDisplaySearchBar);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', updateDisplaySearchBar);
  });

  const updateDisplaySearchBar = () => {
    displaySearchBar.value = window.innerWidth > 768;
  };

  const focusSearch = () => {
    displaySearchBar.value = true;
  };

  const hasVariants = (product: { attributes: any[] }) => {
    return (
      product.attributes.find(
        (attribute: { externalId: string }) => attribute.externalId === 'nb_variants'
      )?.value > 0 && !props.noVariant
    );
  };

  const onSearch = async (button: any) => {
    try {
      if (button) {
        searchButtonClicked.value = true;
      }
      await router.push(localePath({ path: '/search', query: { query: query.value } }));
    } catch (error) {
    } finally {
      closeOptions();
      searchButtonClicked.value = false;
      updateDisplaySearchBar();
    }
  };

  const onFocus = () => {
    if (!searchButtonClicked.value) {
      focused.value = true;
      openOptions.value = true;
    }
  };

  const onBlur = (event: { relatedTarget: any }) => {
    focused.value = false;
    selectedProductId.value = '';
    if (!event.relatedTarget) {
      query.value = '';
    }
    updateDisplaySearchBar();
  };

/*   const clickOption = (product: { product: { externalId: any } }) => {
    router.push(localePath(`/product/${product.product.externalId}`));
    closeOptions();
    searchIncrement.value++;
  }; */

  const closeOptions = () => {
    (document.querySelector('#input') as HTMLElement).blur();
    openOptions.value = false;
    query.value = '';
    updateDisplaySearchBar();
  };

  const selectProduct = async (product: { product: { externalId: string; sku: any } }) => {
    selectedProductId.value = product.product.externalId;
    const response = await getProductVariants({
      productSku: product.product.sku,
      locale,
      pageable: { page: 0, size: 999 },
    });
    variants.value = response?.variants?.content ?? [];
    increment.value++;
    updateDisplaySearchBar();
  };
</script>

<style lang="scss" scoped>
  @use './DjSearchBar.scss';
</style>
